/**
 * settings/types.ts — typed user preferences (v2M4).
 *
 * The Settings type is structured so each future tab in UI_UX §10 owns a
 * sub-object (gameplay, audio, graphics, controls, accessibility, interface).
 * v2M4 ships only `gameplay`; the others are reserved keys that later
 * milestones populate when their backing systems land (M15 graphics,
 * M16 audio, etc.).
 *
 * Persistence shape is identical to runtime shape — no separate
 * "serialised" form is needed because every field is JSON-safe.
 *
 * Schema versioning: bump SETTINGS_SCHEMA_VERSION whenever the shape
 * changes incompatibly. Storage handles version mismatches by falling
 * back to defaults rather than attempting migration (settings are user
 * preferences; recreating them is cheap).
 */

import type { UserSpeed } from '../game/speed-controller';

/**
 * Bumped to 2 in v2M8.8 when AudioSettings landed. Bumped to 3 in
 * v3M19.3 when GameplaySettings.showDamageNumbers landed. Bumped to 4
 * in v4M1.10 when GameplaySettings.pauseOnFocusLoss landed. Bumped to
 * 5 in v4M1.4 when GameplaySettings.autosaveEnabled and
 * autosaveFrequencyTicks landed. Bumped to 6 in v4M1.5 when the
 * Graphics / Controls / Accessibility / Interface sub-objects landed.
 *
 * v4M1.4 made the storage layer additive-merge-tolerant for
 * lower-than-current versions (each sub-object falls through to its
 * defaults on absence), so additive bumps preserve user preferences.
 * Reserve the no-migration / fallback path for shape-breaking bumps.
 */
export const SETTINGS_SCHEMA_VERSION = 6;

export interface GameplaySettings {
  /**
   * Initial sim speed for a new or loaded game. Read by main.ts when
   * starting an in-game session and used to seed the SpeedController.
   */
  defaultGameSpeed: UserSpeed;
  /**
   * Whether the in-game-menu and main-menu Exit actions show a
   * confirm() dialog before tearing down.
   */
  confirmExit: boolean;
  /**
   * Whether the in-game-menu "Return to Main Menu" action shows a
   * confirm() dialog before discarding the current game.
   */
  confirmReturnToMainMenu: boolean;
  /**
   * v3M19.3 — whether the floating damage-number VFX render in combat.
   * When false, the renderer skips draining `gameState.combatEvents`;
   * the sim still publishes events (cheap) but no FloatingTextVfx is
   * scheduled. Default `true` so out-of-the-box players see the new
   * combat readout; can be toggled in the Settings → Gameplay → Combat
   * VFX section.
   */
  showDamageNumbers: boolean;
  /**
   * v4M1.10 — whether to auto-pause the sim when the window loses
   * focus (tab switched away, browser minimised, etc.). Resumes
   * automatically on focus return. Default `true` because most players
   * appreciate not having their game advance while they alt-tab; can
   * be disabled for sessions where the player wants the sim to keep
   * running (e.g. observing AI-only-empire matches).
   */
  pauseOnFocusLoss: boolean;
  /**
   * v4M1.4 — whether the session auto-writes the current game state to
   * the dedicated `autosave` slot every `autosaveFrequencyTicks` sim
   * ticks. Tick-driven rather than wall-clock so a paused game does not
   * keep auto-saving the same frozen snapshot. Default `true`.
   */
  autosaveEnabled: boolean;
  /**
   * v4M1.4 — interval between autosaves, in sim ticks. Default 3000
   * (≈5 min at 1× speed). The autosave fires when
   * `(state.tick - lastAutosaveTick) >= autosaveFrequencyTicks` AND the
   * sim is not paused. Lowering this trades save churn for shorter
   * rollback windows; raising it cuts churn but increases the worst
   * case lost progress on crash.
   */
  autosaveFrequencyTicks: number;
}

/**
 * v2M8.8 — audio bus volumes (AUDIO §3.1 + §11). All four are 0..1
 * multipliers applied to the corresponding gain node:
 *   - master: global volume, on top of music / sfx / ui
 *   - music:  music bus (ambient bed today, more in v3M11)
 *   - sfx:    gameplay SFX bus (weapons, ship lifecycle, structures)
 *   - ui:     UI SFX bus (clicks, hovers, panel transitions, alerts)
 *
 * Per-call attenuation (positional / volume ducking) multiplies on
 * top of these. Master-mute hotkey M and focus-loss fade live in the
 * AudioEngine — they don't touch these slider values.
 */
export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
  ui: number;
}

/**
 * v4M1.5 — Graphics tab (UI_UX §10).
 *
 * `uiScale` preset maps to a CSS root font-size; downstream HUD / panel
 * sizing inherits via `em` (where applicable) or scales explicitly off
 * a CSS variable. `vfxQuality` is the master quality preset; finer
 * `particleDensity` overrides multiply on top. Renderer reads both
 * each frame so changes take effect on the next paint without a
 * session restart.
 *
 * Deferred to a follow-on milestone: explicit resolution / DPR
 * override (DPR is auto-managed today), windowed/fullscreen toggle
 * (needs browser fullscreen-API wiring).
 */
export type UiScalePreset = 'small' | 'default' | 'large';
export type VfxQualityPreset = 'low' | 'medium' | 'high';

export interface GraphicsSettings {
  /**
   * UI scale preset — applied as a CSS root font-size on session
   * mount. `small` = 12px, `default` = 14px, `large` = 17px. Existing
   * DOM panels mostly use absolute pixel sizes, so the impact is
   * scoped to elements that opt into the root scale. Future polish
   * will migrate more elements to `rem`-based sizing.
   */
  uiScale: UiScalePreset;
  /**
   * VFX quality master preset. Multiplies into per-frame counts for
   * tracers, debris, and similar render-only elements. `low` cuts
   * counts to ~0.5×, `high` raises to 1.25×. Combined multiplicatively
   * with `particleDensity` so the player can tune both independently.
   */
  vfxQuality: VfxQualityPreset;
  /**
   * Particle density override slider, 0.25..2.0. Multiplies the
   * `vfxQuality` preset to give finer-grained control over render
   * load. Default 1.0 keeps the v3-shipping behaviour.
   */
  particleDensity: number;
}

/**
 * v4M1.5 — Controls tab (UI_UX §10).
 *
 * MFix42.7 (2026-06-01) — hotkey rebinding now wired via
 * `keyBindings` field below. The legacy hardcoded bindings are
 * preserved as `DEFAULT_KEYBINDINGS` in `keybindings.ts`; an
 * undefined `keyBindings` (or partially-populated map) falls back
 * per-action to those defaults so existing saves are forward-
 * compatible without migration.
 */
export interface ControlsSettings {
  /**
   * Smooth-zoom speed multiplier. 0.5 = half speed, 2.0 = double.
   * Default 1.0 keeps the existing wheel-zoom rate.
   */
  smoothZoomSpeed: number;
  /**
   * When true, the G key behaves as hold-to-preview (release returns
   * to the prior view); when false, G toggles galaxy view on each
   * press. Default `false` matches the legacy toggle behaviour.
   */
  holdGForGalaxyPreview: boolean;
  /**
   * MFix42.7 — per-action key bindings. Keys are `ActionId` literals
   * (see `keybindings.ts`); values are `KeyboardEvent.code` strings
   * optionally prefixed with `Ctrl+` / `Shift+` / `Alt+` modifiers
   * (canonical order). Sparse — actions missing from the map fall
   * back to `DEFAULT_KEYBINDINGS`. Optional so saves predating
   * MFix42.7 deserialise cleanly to the default bindings.
   */
  keyBindings?: Partial<Record<string, string>>;
}

/**
 * v4M1.5 — Accessibility tab (UI_UX §10).
 *
 * Colourblind modes are deferred — they need render-side LUT work or
 * a palette swap pass on the canvas. The other three controls are
 * lightweight and ship now.
 */
export type FontSizePreset = 'small' | 'default' | 'large';

export interface AccessibilitySettings {
  /**
   * Base font size preset for HUD + panel text. Applied as a CSS
   * variable so future panels can opt into rem-based sizing without
   * touching JS layout code.
   */
  fontSize: FontSizePreset;
  /**
   * When true, animations that flash (alert-feed critical border,
   * end-game stinger flash) are dampened to a static highlight.
   * Default `false` — flashing is on by default for visibility.
   */
  flashReduction: boolean;
  /**
   * When true, tooltips persist until dismissed rather than fading on
   * mouseout. Native `title=` tooltips ignore this in v4M1.5; the
   * setting is recorded against the structured-tooltip widget that
   * lands in v4M1.11.
   */
  tooltipLingering: boolean;
}

/**
 * v4M1.5 — Interface tab (UI_UX §10).
 *
 * `mutedAlertSources` is a list of source identifiers that the
 * alert-feed filters out. v4M1.5 ships the storage + filter side;
 * right-click-to-mute on a toast (which appends to this list) is a
 * follow-on item. The Settings tab exposes a read-only list with
 * un-mute buttons so a player who somehow ends up with a stale list
 * can clear it.
 *
 * Source records mirror the existing `AlertSource` shape used by the
 * alert-feed for click-to-jump. Identity comparison is by kind+id pair.
 */
export type MinimapPosition =
  | 'bottom-left'
  | 'bottom-right'
  | 'top-left'
  | 'top-right';

export interface MutedAlertSource {
  kind: 'planet' | 'system' | 'ship';
  id: number;
}

export interface InterfaceSettings {
  /**
   * Minimap (mini-galaxy) corner anchor. Default `bottom-left` matches
   * the legacy mount position.
   */
  minimapPosition: MinimapPosition;
  /**
   * Maximum number of alert toasts visible simultaneously. Older
   * toasts drop off when the count is reached. Three presets — 3,
   * 5 (legacy), 7. Stored as the integer count rather than a preset
   * key so the alert-feed reads it directly.
   */
  alertFeedMaxVisible: 3 | 5 | 7;
  /**
   * When true, single-click on a planet / ship in the system view
   * auto-opens the detail panel. When false, the player has to
   * explicitly open the panel. Default `true` preserves v3 behaviour.
   */
  autoOpenPanelsOnSelection: boolean;
  /**
   * v4M1.5 — alert-source mute list. Identity by kind+id pair. The
   * alert-feed drops events whose source matches any entry. Empty by
   * default; populated as right-click-to-mute lands on toasts in a
   * future polish pass.
   */
  mutedAlertSources: MutedAlertSource[];
}

/**
 * Top-level Settings record. v4M1.5 populated the four previously
 * reserved sub-objects (graphics, controls, accessibility,
 * interface) with shipped controls.
 */
export interface Settings {
  schemaVersion: number;
  gameplay: GameplaySettings;
  audio: AudioSettings;
  graphics: GraphicsSettings;
  controls: ControlsSettings;
  accessibility: AccessibilitySettings;
  interface: InterfaceSettings;
}

export const DEFAULT_GAMEPLAY_SETTINGS: GameplaySettings = {
  defaultGameSpeed: 1,
  confirmExit: true,
  confirmReturnToMainMenu: true,
  showDamageNumbers: true,
  pauseOnFocusLoss: true,
  autosaveEnabled: true,
  autosaveFrequencyTicks: 3000,
};

export const DEFAULT_AUDIO_SETTINGS: AudioSettings = {
  master: 0.7,
  music: 0.5,
  sfx: 0.7,
  ui: 0.7,
};

export const DEFAULT_GRAPHICS_SETTINGS: GraphicsSettings = {
  uiScale: 'default',
  vfxQuality: 'medium',
  particleDensity: 1.0,
};

export const DEFAULT_CONTROLS_SETTINGS: ControlsSettings = {
  smoothZoomSpeed: 1.0,
  holdGForGalaxyPreview: false,
};

export const DEFAULT_ACCESSIBILITY_SETTINGS: AccessibilitySettings = {
  fontSize: 'default',
  flashReduction: false,
  tooltipLingering: false,
};

export const DEFAULT_INTERFACE_SETTINGS: InterfaceSettings = {
  minimapPosition: 'bottom-left',
  alertFeedMaxVisible: 5,
  autoOpenPanelsOnSelection: true,
  mutedAlertSources: [],
};

export const DEFAULT_SETTINGS: Settings = {
  schemaVersion: SETTINGS_SCHEMA_VERSION,
  gameplay: { ...DEFAULT_GAMEPLAY_SETTINGS },
  audio: { ...DEFAULT_AUDIO_SETTINGS },
  graphics: { ...DEFAULT_GRAPHICS_SETTINGS },
  controls: { ...DEFAULT_CONTROLS_SETTINGS },
  accessibility: { ...DEFAULT_ACCESSIBILITY_SETTINGS },
  interface: { ...DEFAULT_INTERFACE_SETTINGS },
};
